import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Award, Loader2, Copy, Check, Compass, BookOpen, MessageCircle, Send } from "lucide-react";

/**
 * PublicWall — the shareable /wall/:identifier page.  Renders every
 * completed adventure as a miniature card, plus totals and a share
 * link.  Anonymous.  Identifier resolves to username → email-prefix →
 * user id on the backend.
 */
export default function PublicWall() {
  const { identifier } = useParams();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.get(`/wall/${encodeURIComponent(identifier)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ notfound: true }); });
    return () => { cancelled = true; };
  }, [identifier]);

  useEffect(() => {
    if (!data || data.notfound) return;
    document.title = `${data.learner.display_name}'s wall · Durga Programming Adventures`;
  }, [data]);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/wall/${identifier}` : "";
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Wall link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy."); }
  };

  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (data.notfound) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="public-wall-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-3">No learner by that name.</h1>
        <Link to="/adventures" className="btn-outline-ink text-sm">Browse adventures</Link>
      </div>
    );
  }

  const empty = (data.completions || []).length === 0;

  return (
    <div className="container-narrow py-10 md:py-14" data-testid={`public-wall-${identifier}`}>
      <div className="mb-8 flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <Award className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Trophy wall</div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-ink leading-[1.05] mb-2" data-testid="public-wall-name">
            {data.learner.display_name}
          </h1>
          <p className="text-ink2">
            {empty
              ? "hasn't finished any adventures yet — but the road is long."
              : `has walked ${data.total} of Durga Programming Adventures' expeditions.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onCopy} className="btn-outline-ink text-xs inline-flex items-center gap-1.5" data-testid="public-wall-copy">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
          </button>
        </div>
      </div>

      {empty ? (
        <div className="rounded-3xl border-2 border-dashed border-ink/30 p-10 text-center text-ink2" data-testid="public-wall-empty">
          <p className="italic mb-3">This wall is bare.  Every certificate hangs itself once you finish an adventure.</p>
          <Link to="/adventures" className="btn-primary text-sm inline-flex items-center gap-1.5">
            <Compass className="w-4 h-4" /> Start walking <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-wall-grid">
          {data.completions.map((c) => (
            <WallCard key={c.slug} c={c} />
          ))}
        </div>
      )}

      <Guestbook identifier={identifier} learnerName={data.learner.display_name} />

      <div className="mt-10 text-center text-xs text-ink2/70">
        Durga Programming Adventures · <Link to="/" className="underline hover:text-marigold">start your own</Link>
      </div>
    </div>
  );
}

function Guestbook({ identifier, learnerName }) {
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState("");
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/wall/${encodeURIComponent(identifier)}/guestbook?limit=30`)
      .then(({ data }) => { if (!cancelled) setNotes(data.notes || []); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [identifier]);

  const remaining = 140 - note.length;
  const canSubmit = note.trim().length >= 3 && note.trim().length <= 140 && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wall/${encodeURIComponent(identifier)}/guestbook`, {
        note: note.trim(),
        author_name: author.trim() || null,
        author_email: email.trim() || null,
      });
      // Prepend the new note optimistically
      setNotes((prev) => [data.note, ...(prev || [])]);
      setNote("");
      setEmail("");
      toast.success(email.trim() ? "Left a note. If they reply, you'll hear back." : "Left a note. Thank you for the warmth.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't leave the note.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-14" data-testid="wall-guestbook">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Guestbook</div>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-ink leading-tight">
            Leave {learnerName} a warm one-liner.
          </h2>
          <p className="text-sm text-ink2 mt-1 max-w-xl">
            One line, no signup, one signal per person per day.  No links, no slurs — just something you'd say if you were passing by.
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="rounded-2xl border-2 border-ink bg-cream shadow-[5px_5px_0_0_#1F1A17] p-5 mb-6"
        data-testid="wall-guestbook-form"
      >
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 200))}
          placeholder={`Something warm for ${learnerName}…`}
          rows={2}
          maxLength={200}
          className="w-full bg-transparent border-b border-ink/20 focus:border-marigold focus:outline-none pb-2 text-ink font-heading text-base resize-none"
          data-testid="wall-guestbook-note"
        />
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <input
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value.slice(0, 40))}
            placeholder="Your name (or leave blank — you'll be 'A passer-by')"
            maxLength={40}
            className="flex-1 min-w-[220px] bg-transparent border border-ink/25 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-marigold"
            data-testid="wall-guestbook-author"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.slice(0, 120))}
            placeholder="Email (optional — for reply)"
            maxLength={120}
            className="flex-1 min-w-[220px] bg-transparent border border-ink/25 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-marigold"
            data-testid="wall-guestbook-email"
          />
          <div className={`text-[10px] font-mono uppercase tracking-widest ${remaining < 20 ? "text-marigold" : "text-ink2/60"}`}>
            {remaining} left
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wall-guestbook-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Leave it
          </button>
        </div>
      </form>

      {notes === null ? (
        <div className="py-6 text-center text-ink2 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink/25 p-6 text-center text-ink2 italic text-sm" data-testid="wall-guestbook-empty">
          No notes yet — be the first to say hello.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="wall-guestbook-list">
          {notes.map((n, i) => (
            <li
              key={n.note_id || `${n.created_at}-${i}`}
              className="rounded-2xl border border-ink/25 bg-cream p-4 hover:border-marigold/60 transition"
              data-testid={`wall-guestbook-note-${i}`}
            >
              <div className="text-ink font-heading text-base leading-snug">
                <span className="text-marigold mr-1">“</span>{n.note}<span className="text-marigold ml-1">”</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/60">
                <span>{n.author_name || "A passer-by"}</span>
                <span>·</span>
                <span className="font-mono normal-case tracking-normal">
                  {formatGuestbookDay(n.created_at || n.day)}
                </span>
              </div>
              {n.owner_reply && (
                <div
                  className="mt-3 ml-6 border-l-2 border-marigold pl-3 py-1"
                  data-testid={`wall-guestbook-reply-${i}`}
                >
                  <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold mb-0.5">
                    {learnerName} replied
                  </div>
                  <div className="text-sm text-ink font-heading italic leading-snug">
                    “{n.owner_reply}”
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatGuestbookDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return "today";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

function WallCard({ c }) {
  const dateStr = c.completed_at ? new Date(c.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  const roman = (c.kicker || "").split(" ").pop();
  const dest = c.share_token ? `/c/${c.share_token}` : `/adventures/${c.slug}`;
  return (
    <Link
      to={dest}
      className="group relative block rounded-2xl border-2 border-ink bg-cream shadow-[5px_5px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[9px_9px_0_0_#1F1A17] transition-all overflow-hidden p-5 pb-12"
      data-testid={`public-wall-card-${c.slug}`}
    >
      <div className="absolute top-2 right-4 font-heading text-[5rem] font-semibold text-ink/[0.05] leading-none select-none pointer-events-none">{roman}</div>
      <div className="absolute top-2 left-2 w-3 h-3 border-l-2 border-t-2 border-marigold" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-r-2 border-b-2 border-marigold" />

      <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold mb-2 relative">
        <Award className="w-3 h-3 inline mr-1" /> {c.kicker}
      </div>
      <div className="font-heading text-base font-semibold text-ink leading-snug mb-2 relative pr-8 line-clamp-2 group-hover:text-marigold transition">
        {c.title}
      </div>
      <div className="text-[10px] font-mono text-ink2/70 uppercase tracking-[0.16em] relative">
        {dateStr}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-marigold/10 border-t border-marigold/30 flex items-center px-4 text-[10px] uppercase tracking-[0.24em] font-bold text-ink2">
        <BookOpen className="w-3 h-3 mr-1" /> open certificate
      </div>
    </Link>
  );
}
